export const CARE_REGION_KEY = 'heir:care:region:v1';
export const CARE_REGIONS = Object.freeze(['china', 'north-america', 'south-america', 'other']);

export function normalizeCareRegion(value) {
  return CARE_REGIONS.includes(value) ? value : '';
}

export function readCareRegion(storage) {
  try { return normalizeCareRegion(storage?.getItem(CARE_REGION_KEY)); } catch { return ''; }
}

export function saveCareRegion(storage, value) {
  const region = normalizeCareRegion(value);
  if (!region) return false;
  try { storage?.setItem(CARE_REGION_KEY, region); return Boolean(storage); } catch { return false; }
}

export function safeNativeSelector(value) {
  const selector = typeof value === 'string' ? value.trim() : '';
  return /^(?:#[a-zA-Z][\w-]*|[a-z][a-z0-9]*(?:-[a-z0-9]+)+)$/.test(selector) ? selector : '';
}

export function careChannelOrder(region) {
  return normalizeCareRegion(region) === 'china' ? ['wechat', 'native'] : ['native', 'wechat'];
}

export function careRegionLabel(region, locale = 'en') {
  const labels = {
    china: ['China', '中国'],
    'north-america': ['North America', '北美'],
    'south-america': ['South America', '南美'],
    other: ['Elsewhere', '其他地区'],
  };
  return labels[normalizeCareRegion(region)]?.[/^zh(?:-|$)/i.test(locale) ? 1 : 0] || '';
}

export function initCareRouter(root) {
  if (!root || root.dataset.careReady) return null;
  const doc = root.ownerDocument;
  const win = doc.defaultView;
  const find = (selector) => root.querySelector(selector);
  const dialog = find('[data-care-dialog]');
  const launcher = find('[data-care-open]');
  const steps = Object.fromEntries(['region', 'channels', 'chat'].map((step) => [step, find('[data-care-step="' + step + '"]')]));
  const mount = find('[data-care-native-mount]');
  const feedback = find('[data-care-native-feedback]');
  const nativeStatus = find('[data-care-native-status]');
  const retry = find('[data-care-retry]');
  const nativeButton = find('[data-care-native]');
  const back = find('[data-care-back]');
  const navigation = find('[data-care-navigation]');
  if (!win || !dialog || !launcher || Object.values(steps).some((step) => !step) || !mount || !feedback || !nativeStatus || !retry || !nativeButton || typeof dialog.showModal !== 'function') return null;

  root.dataset.careReady = 'true';
  const locale = root.dataset.locale || doc.documentElement.lang || 'en';
  const zh = /^zh(?:-|$)/i.test(locale);
  const t = (en, cn) => zh ? cn : en;
  const nativeSelector = safeNativeSelector(root.dataset.nativeSelector);
  const regions = Array.from(root.querySelectorAll('[data-care-region]'));
  const regionNames = Array.from(root.querySelectorAll('[data-care-region-name]'));
  const cleanup = [];
  const awaitingDefinition = new WeakSet();
  let storage;
  try { storage = win.sessionStorage; } catch { storage = undefined; }
  let region = readCareRegion(storage);
  let state = 'closed';
  let previousFocus = launcher;
  let disposed = false;
  let nativeHost = null;
  let original = null;
  let intent = 0;
  let pendingIntent = null;
  let openingIntent = null;
  let openedIntent = null;
  let programmaticClose = false;
  let timer = null;

  const on = (element, type, listener) => {
    if (!element) return;
    element.addEventListener(type, listener);
    cleanup.push(() => element.removeEventListener(type, listener));
  };
  const attr = (element, name, value) => {
    if (value === null) element.removeAttribute(name);
    else if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  };
  const stopTimer = () => { if (timer !== null) win.clearTimeout(timer); timer = null; };

  function status(value) {
    root.dataset.nativeState = value;
    root.dataset.nativeReady = String(value === 'ready');
    feedback.hidden = value === 'ready';
    retry.hidden = value === 'loading';
    mount.setAttribute('aria-busy', String(value === 'loading'));
    const copy = value === 'loading'
      ? t('Opening your conversation…', '正在打开对话…')
      : t('Chat could not open yet. Try again, or contact us on WeChat.', '暂时无法打开聊天。请重试，或通过微信客服咨询。');
    if (nativeStatus.textContent !== copy) nativeStatus.textContent = copy;
  }

  function gateHost(hidden) {
    if (!nativeHost) return;
    attr(nativeHost, 'data-heir-care-gated', String(hidden));
    nativeHost.inert = hidden;
  }

  function hideNative() {
    gateHost(true);
    programmaticClose = true;
    // Use the verified host's public API, never its iframe contents.
    try { if (nativeHost?.open && typeof nativeHost.close === 'function') nativeHost.close(); } catch { /* The outer gate remains closed. */ }
    finally { programmaticClose = false; }
    openedIntent = null;
  }

  function cancelRequest() {
    intent += 1;
    pendingIntent = null;
    openingIntent = null;
    stopTimer();
  }

  function nativeClose() {
    if (!disposed && !programmaticClose && state === 'chat' && openedIntent === intent) close();
  }

  function releaseHost(restorePosition = true) {
    const host = nativeHost, saved = original;
    nativeHost = null;
    original = null;
    if (!host || !saved) return;
    host.removeEventListener('shopify-chat:close', nativeClose);
    try { if (host.open && typeof host.close === 'function') host.close(); } catch { /* Always restore the outer DOM and attributes. */ }
    if (restorePosition) {
      if (saved.marker.parentNode) saved.marker.parentNode.insertBefore(host, saved.marker);
      else if (saved.parent?.isConnected) saved.parent.insertBefore(host, saved.next?.parentNode === saved.parent ? saved.next : null);
    }
    saved.marker.remove();
    for (const [name, value] of saved.attributes) attr(host, name, value);
    host.inert = saved.inert;
    if (restorePosition && saved.open) {
      try { if (typeof host.show === 'function') host.show(); } catch { /* Do not block teardown. */ }
    }
  }

  function usableHost(candidate) {
    if (!candidate || candidate === doc.body || candidate === doc.documentElement || candidate === root || candidate.contains(root)) return false;
    return !root.contains(candidate) || mount.contains(candidate);
  }

  function adoptHost(candidate) {
    if (candidate === nativeHost || !usableHost(candidate)) return;
    const parent = candidate.parentNode;
    if (!parent) return;
    const marker = doc.createComment('heir-care-native-position');
    original = {
      parent, next: candidate.nextSibling, marker,
      attributes: ['mode', 'mode-switch', 'data-heir-care-gated', 'data-heir-care-mounted'].map((name) => [name, candidate.getAttribute(name)]),
      inert: Boolean(candidate.inert), open: candidate.open === true,
    };
    nativeHost = candidate;
    parent.insertBefore(marker, candidate);
    gateHost(true);
    attr(candidate, 'mode', 'managed');
    // Omit mode-switch: it enables the provider's mobile standalone override.
    attr(candidate, 'mode-switch', null);
    attr(candidate, 'data-heir-care-mounted', 'true');
    if (candidate.parentNode !== mount) mount.append(candidate);
    candidate.addEventListener('shopify-chat:close', nativeClose);
    hideNative();
  }

  function findNativeHost() {
    let candidate = null;
    try { candidate = nativeSelector ? doc.querySelector(nativeSelector) : null; } catch { candidate = null; }
    if (!usableHost(candidate)) candidate = null;
    if (candidate !== nativeHost) {
      const lostHost = Boolean(nativeHost);
      releaseHost(false);
      if (candidate) adoptHost(candidate);
      if (lostHost && state === 'chat') {
        intent += 1;
        pendingIntent = intent;
        openingIntent = null;
        openedIntent = null;
        status('loading');
        armTimeout();
      }
    }
    return nativeHost;
  }

  function armTimeout() {
    stopTimer();
    const expected = intent;
    timer = win.setTimeout(() => {
      timer = null;
      if (!disposed && state === 'chat' && expected === intent && pendingIntent !== null) status('unavailable');
    }, 10000);
  }

  function failOpen(expected, host) {
    if (disposed || expected !== intent || state !== 'chat' || host !== nativeHost) return;
    pendingIntent = null;
    openingIntent = null;
    openedIntent = null;
    stopTimer();
    hideNative();
    status('unavailable');
  }

  function opened(expected, host) {
    if (disposed || expected !== intent || state !== 'chat' || host !== nativeHost) return;
    openedIntent = expected;
    openingIntent = null;
    finishFirstPaint();
  }

  function finishFirstPaint() {
    if (disposed || state !== 'chat' || openedIntent !== intent || !nativeHost?.open || !nativeHost.hasAttribute('shell-ready')) return;
    pendingIntent = null;
    stopTimer();
    // shell-ready signals native first paint, not message delivery/staff presence.
    status('ready');
  }

  function tryRequestedOpen() {
    if (disposed || state !== 'chat' || pendingIntent !== intent || openingIntent === intent || openedIntent === intent) return;
    const host = nativeHost;
    if (!host) return;
    if (typeof host.show !== 'function' || typeof host.close !== 'function') {
      const tag = host.localName;
      if (tag?.includes('-') && win.customElements?.whenDefined && !awaitingDefinition.has(host)) {
        awaitingDefinition.add(host);
        Promise.resolve(win.customElements.whenDefined(tag)).then(() => {
          if (!disposed && nativeHost === host) tryRequestedOpen();
        }).catch(() => failOpen(intent, host));
      }
      return;
    }
    const expected = intent;
    openingIntent = expected;
    gateHost(false);
    try {
      const result = host.show();
      if (result && typeof result.then === 'function') result.then(() => opened(expected, host), () => failOpen(expected, host));
      else opened(expected, host);
    } catch { failOpen(expected, host); }
  }

  function updateView(step) {
    state = step;
    root.dataset.careStep = step;
    dialog.dataset.careStep = step;
    for (const [name, element] of Object.entries(steps)) element.hidden = name !== step;
    if (navigation) navigation.hidden = step === 'region' || step === 'closed';
    if (back) back.hidden = step === 'region' || step === 'closed';
    const title = step === 'chat' ? 'HeirCareChatTitle' : step === 'channels' ? 'HeirCareChannelTitle' : 'HeirCareTitle';
    dialog.setAttribute('aria-labelledby', title);
    for (const element of regionNames) {
      const label = careRegionLabel(region, locale);
      if (element.textContent !== label) element.textContent = label;
    }
    if (dialog.open && step !== 'closed') find('#' + title)?.focus({ preventScroll: true });
  }

  function setStep(step) {
    cancelRequest();
    hideNative();
    updateView(step);
    if (step !== 'chat') return;
    pendingIntent = intent;
    status('loading');
    armTimeout();
    findNativeHost();
    tryRequestedOpen();
  }

  function selectRegion(value) {
    region = normalizeCareRegion(value);
    for (const button of regions) button.setAttribute('aria-pressed', String(button.dataset.careRegion === region));
  }

  function open(event) {
    event?.preventDefault();
    previousFocus = event?.currentTarget || doc.activeElement || launcher;
    setStep('region');
    if (!dialog.open) dialog.showModal();
    doc.documentElement.classList.add('heir-care-modal-open');
    launcher.setAttribute('aria-expanded', 'true');
    dialog.scrollTop = 0;
    find('#HeirCareTitle')?.focus({ preventScroll: true });
  }

  function close() {
    cancelRequest();
    hideNative();
    updateView('closed');
    if (dialog.open) dialog.close();
  }

  launcher.setAttribute('aria-haspopup', 'dialog');
  launcher.setAttribute('aria-controls', dialog.id);
  launcher.setAttribute('aria-expanded', 'false');
  // China can enter the same loading/error-aware chat view before Inbox loads.
  nativeButton.disabled = false;
  on(launcher, 'click', open);
  on(find('[data-care-close]'), 'click', close);
  on(back, 'click', () => setStep('region'));
  for (const button of regions) on(button, 'click', () => {
    selectRegion(button.dataset.careRegion);
    if (!region) return;
    saveCareRegion(storage, region);
    setStep(region === 'china' ? 'channels' : 'chat');
  });
  on(nativeButton, 'click', () => { if (region && state === 'channels') setStep('chat'); });
  on(retry, 'click', () => { if (region && state === 'chat') setStep('chat'); });
  on(dialog, 'close', () => {
    if (dialog.open) return;
    cancelRequest();
    hideNative();
    updateView('closed');
    doc.documentElement.classList.remove('heir-care-modal-open');
    launcher.setAttribute('aria-expanded', 'false');
    const target = previousFocus?.isConnected && !previousFocus.closest('[hidden]') ? previousFocus : launcher;
    target.focus({ preventScroll: true });
  });
  on(dialog, 'cancel', (event) => { event.preventDefault(); close(); });
  // Inbox forwards Escape from its frame as a composed synthetic keydown;
  // the browser does not turn that synthetic event into dialog cancellation.
  on(dialog, 'keydown', (event) => {
    if (event.key === 'Escape') { event.preventDefault(); close(); }
  });
  on(dialog, 'click', (event) => {
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
  });
  // Native <dialog> lets Tab enter the cross-origin chat frame. Do not replace
  // its focus navigation with a trap limited to our light-DOM buttons.
  selectRegion(region);
  updateView('closed');
  status('loading');
  findNativeHost();
  const observer = nativeSelector && typeof win.MutationObserver === 'function' ? new win.MutationObserver((records = []) => {
    if (disposed) return;
    findNativeHost();
    tryRequestedOpen();
    finishFirstPaint();
    const nativeClosed = records.some((record) => record.target === nativeHost && record.type === 'attributes' && record.attributeName === 'open');
    // A provider load failure can also remove open. Only the explicit native
    // close event closes our dialog; a silent failure stays here with retry.
    if (nativeClosed && state === 'chat' && openedIntent === intent && nativeHost?.open === false) failOpen(intent, nativeHost);
  }) : null;
  observer?.observe(doc.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['open', 'shell-ready'] });
  const destroy = () => {
    if (disposed) return;
    disposed = true;
    observer?.disconnect();
    close();
    for (const remove of cleanup) remove();
    releaseHost(true);
    delete root.dataset.careReady;
    doc.documentElement.classList.remove('heir-care-modal-open');
  };
  on(root.closest('.shopify-section'), 'shopify:section:unload', destroy);
  return { destroy };
}

if (typeof document !== 'undefined') {
  const init = (scope = document) => {
    const roots = scope.matches?.('[data-heir-care]') ? [scope] : scope.querySelectorAll('[data-heir-care]');
    roots.forEach(initCareRouter);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
}
